/**
 * @ngdoc component
 * @name addComment
 * @module flowingly.components
 * @description This is a component for adding a new comment / feedback to flow / flow model
 *
 * Input attributes
 * commentTargetId - the id of comment target
 *
 * Output attributes
 * onAddComment - callback when add a new comment
 *
 * @usage
 * ```
 * <add-comment comment-target-type="1" comment-target-id="$ctrl.commentTargetId" load-comments="$ctrl.loadComments() on-add-comment="$ctrl.onAddComment({ comment: comment })"></add-comment>
 * ```
 */

import angular, { IScope, ITimeoutService } from 'angular';
import {
  IComment,
  CommentType,
  CommentSource
} from '../../interfaces/comment.interface';
import { ICommentMention } from '../../interfaces/comment-mention.interface';
import { FlowinglyMentionService } from '@Shared.Angular/flowingly.services/flowingly.mention.service';
import { CommentApiService } from '@Shared.Angular/flowingly.services/comment.api.service';
import { Services } from '@Shared.Angular/@types/services';
import { ModelerActionEvent } from '../../../../Flowingly.Modeler.Web/Client/@types/modelerActionEvent';
import CommentTargetType from '@Shared.Angular/@types/core/contracts/constants/commentTargetType';

export class AddCommentComponentController {
  static $inject = [
    '$timeout',
    '$scope',
    'flowinglyMentionService',
    'commentApiService',
    'notificationService',
    'sessionService',
    'validationService',
    'fileService',
    'pubsubService',
    'tokenService'
  ];
  public static COMMENT_RESET_EVENT = 'COMMENT_RESET_EVENT';
  public commentTargetId: string;
  public commentTargetType: string;
  public flowOwnerOnly: boolean;
  public addCommentHeader: string;
  public userId: string;
  public loadComments: () => void;
  public onAddComment: (comment) => void;
  public debounceInterval = 100;
  private commentContent: string;
  private comment: IComment;
  private isAddButtonDisabled: boolean;
  public fileList: any[] = [];
  public commentTargetTypeFromReact: string;
  public isPublishRequestSchema: boolean;
  public commentSource: CommentSource;

  constructor(
    private $timeout: ITimeoutService,
    private $scope: IScope,
    private flowinglyMentionService: FlowinglyMentionService,
    private commentApiService: CommentApiService,
    private notificationService: Services.NotificationService,
    private sessionService: Services.SessionService,
    private validationService: Services.ValidationService,
    private fileService: Services.FileService,
    private pubsubService: Services.PubSubService,
    private tokenService: Services.TokenService
  ) {
    if (
      this.commentTargetType === CommentTargetType.FLOW.toString() ||
      this.commentTargetType === CommentTargetType.STEP_TASK.toString()
    ) {
      this.addCommentHeader = 'Add a comment';
    } else {
      this.addCommentHeader = 'Add feedback';
    }

    this.flowOwnerOnly = this.flowOwnerOnly || false;
  }

  $onInit() {
    this.pubsubService.subscribe(
      'SIGNALR_RUNNER_COMPLETE_STEP',
      () => {
        this.removeAllFiles();
      },
      'add-comment.component'
    );

    this.pubsubService.subscribe(
      'FILEUPLOAD_UPLOAD_STARTED',
      () => {
        this.disableAddButton();
      },
      'add-comment.component'
    );

    this.pubsubService.subscribe(
      'FILEUPLOAD_UPLOAD_COMPLETED',
      () => {
        this.enableAddButton();
      },
      'add-comment.component'
    );

    this.pubsubService.subscribe(
      'FILEUPLOAD_UPLOAD_FAILED',
      () => {
        this.enableAddButton();
      },
      'add-comment.component'
    );
  }

  addComment(): void {
    this.$timeout(() => {
      const mentions: ICommentMention[] =
        this.flowinglyMentionService.extractMentionsFromCommentText(
          this.commentContent || ''
        );
      const commentContents: string =
        this.flowinglyMentionService.transformMentionDisplayToStore(
          this.commentContent || ''
        );
      let confirmText, warningText;

      if (
        this.commentTargetType === CommentTargetType.FLOW.toString() ||
        this.commentTargetType === CommentTargetType.STEP_TASK.toString()
      ) {
        confirmText = 'Comment added';
        warningText = 'Cannot add empty comment';
      } else {
        confirmText = 'Feedback added';
        warningText = 'Cannot add empty feedback';
      }

      if (this.flowinglyMentionService.trimSpaces(commentContents) === '') {
        this.notificationService.showWarningToast(warningText);
        return;
      } else if (
        this.validationService.isXssVulnerableString(commentContents)
      ) {
        this.notificationService.showErrorToast(
          'The comment field contains invalid text. Please remove any markup or script from the field.'
        );
        return;
      }
      this.comment = {
        comment: commentContents,
        mentions: mentions,
        fileIds: this.fileList.map((f) => f.id),
        userId: this.sessionService.getUser().id,
        flowOwnerOnly: this.flowOwnerOnly,
        commentType: CommentType.UserComment,
        source: this.commentSource,
        flowModelVersion: ''
      };

      this.commentApiService
        .addFlowComment(
          parseInt(
            this.commentTargetTypeFromReact == null
              ? this.commentTargetType
              : this.commentTargetTypeFromReact
          ),
          this.commentTargetId,
          this.comment,
          this.isPublishRequestSchema
        )
        .then(() => {
          this.$scope.$broadcast(
            AddCommentComponentController.COMMENT_RESET_EVENT
          );

          // notify parent component of the new comment just added.
          // As data is not from backend, need manually fill some of the details
          const currentUser = this.sessionService.getUser();
          const newComment: IComment = {
            fullName: currentUser.fullName,
            avatarUrl: '',
            userId: currentUser.id,
            commentedByName: currentUser.fullName,
            momentCommented: 'A Few Seconds Ago',
            comment: commentContents,
            files: this.fileList,
            commentType: CommentType.UserComment,
            source: this.commentSource,
            flowModelVersion: ''
          };
          this.onAddComment({ comment: newComment });
          this.fileList = [];

          this.notificationService.showSuccessToast(confirmText);
        });
    }, this.debounceInterval);
  }

  closeComment(): void {
    this.pubsubService.publish(
      'MODELER_ACTION_EVENT',
      ModelerActionEvent.TOGGLE_FEEDBACK_PANEL
    );
  }

  onFileUploaded(file) {
    this.fileList.push(file);
  }

  disableAddButton() {
    this.isAddButtonDisabled = true;
  }

  enableAddButton() {
    this.isAddButtonDisabled = false;
  }

  removeFile(index) {
    this.disableAddButton();
    const file = this.fileList.splice(index, 1)[0];
    const user = this.sessionService.getUser();
    this.fileService.setUser(user.id, this.tokenService.getTenant().id);
    this.fileService
      .removeFileForId(file.id)
      .then(() => {
        this.notificationService.showSuccessToast(file.filename + ' Removed');
        this.enableAddButton();
      })
      .catch(() => this.enableAddButton());
  }

  removeAllFiles() {
    if (!this.fileList || this.fileList.length === 0) return;

    const fileIds = this.fileList.map((f) => f.id);
    fileIds.forEach((id) => this.fileService.removeFileForId(id));

    this.fileList = [];
  }

  $onDestroy() {
    this.pubsubService.unsubscribeAll('add-comment.component');
    this.removeAllFiles();
  }
}

export class AddCommentComponent implements angular.IComponentOptions {
  public bindings: Bindings;
  public templateUrl: string;

  /* TODO: 2 way binding of commentTargetType isn't working when in react compoenent. 
      Adding a workaround until we re-write this in react or figure out why this commentTargetType bidning needs to be a 2 way binding
      it doesnt look like it needs to be but it breaks the component. Leaving this re-visit later */

  constructor() {
    this.bindings = {
      commentTargetId: '<',
      commentTargetType: '@',
      flowOwnerOnly: '<',
      onAddComment: '&',
      commentTargetTypeFromReact: '<',
      isPublishRequestSchema: '<?',
      commentSource: '<'
    };

    this.templateUrl = 'add-comment.tmpl.html';
  }

  controller = AddCommentComponentController;
}

angular
  .module('flowingly.components')
  .component('addComment', new AddCommentComponent());
